import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAccountant } from "@/lib/roles";
import { Page, Card, money, Loading } from "@/v2/ui";
import NewShiftButton from "@/v2/NewShift";

export default function Dashboard2() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState(null);
  const [bank, setBank] = useState(null);
  const [debtors, setDebtors] = useState(null);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/shifts").then(({ data }) => setShifts(data)).catch(() => setShifts([]));
    api.get("/banking/status").then(({ data }) => setBank(data)).catch(() => setBank(null));
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
    if (isAccountant(user)) api.get("/receivables/accounts").then(({ data }) => setDebtors(data)).catch(() => setDebtors(null));
  }, [user]);

  if (!shifts) return <Page title="Dashboard"><Loading /></Page>;
  const count = (st) => shifts.filter((s) => st.includes(s.status)).length;
  const month = new Date().toISOString().slice(0, 7);
  const approvedThisMonth = shifts.filter((s) => ["approved", "locked"].includes(s.status) && s.date.startsWith(month)).length;

  // fuel in tanks: each tank's latest closing dip
  const tankRows = [];
  if (cfg) {
    for (const t of cfg.tanks.filter((x) => x.active !== false)) {
      const s = shifts.find((x) => (x.tank_dips || []).some((d) => d.tank_id === t.id && Number(d.closing_dip)));
      const d = s && s.tank_dips.find((x) => x.tank_id === t.id);
      tankRows.push({ label: t.label, product: cfg.products.find((p) => p.id === t.product_id)?.name || "", litres: d ? Number(d.closing_dip) : null });
    }
  }
  const byProduct = {};
  tankRows.forEach((r) => { if (r.litres != null) byProduct[r.product] = (byProduct[r.product] || 0) + r.litres; });

  const acc = debtors?.accounts || [];
  const cards = acc.filter((a) => a.kind === "card").reduce((x, a) => x + a.balance, 0);
  const over30 = acc.reduce((x, a) => x + a.ageing.d31_60 + a.ageing.d61_90 + a.ageing.d90_plus, 0);

  return (
    <Page title="Dashboard" actions={<NewShiftButton />}>
      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <Card title="Shifts">
          <div className="mt-3 flex flex-col text-sm">
            <Link to="/v2/shifts?status=draft" className="flex justify-between border-b border-slate-100 py-2.5 hover:text-sky-700"><span>Draft</span><span className="font-semibold">{count(["draft"])}</span></Link>
            <Link to="/v2/shifts?status=submitted" className="flex justify-between border-b border-slate-100 py-2.5 hover:text-sky-700"><span>Awaiting approval</span><span className="font-semibold">{count(["submitted", "manager_reviewed"])}</span></Link>
            <Link to="/v2/shifts?status=approved" className="flex justify-between py-2.5 hover:text-sky-700"><span>Approved this month</span><span className="font-semibold">{approvedThisMonth}</span></Link>
          </div>
        </Card>
        <Card title="Cash to bank">
          <div className="mt-3 text-[28px] font-semibold text-slate-900">KES {money(bank?.cash_on_hand || 0)}</div>
          <div className="mt-1 text-sm text-slate-500">{bank?.lpg ? "Fuel & lubes · " : ""}{bank?.shifts?.length || 0} shifts</div>
          {bank?.lpg && (
            <div className="mt-3 flex justify-between border-t border-slate-100 pt-3 text-sm"><span>LPG cash</span><span className="font-semibold tabular-nums">KES {money(bank.lpg.cash_on_hand)}</span></div>
          )}
          <Link to="/v2/banking" className="mt-3 inline-block text-sm font-semibold text-sky-700 hover:underline">Record banking</Link>
        </Card>
        {debtors && (
          <Card title="Money owed to you">
            <div className="mt-3 text-[28px] font-semibold text-slate-900">KES {money(debtors.total_balance)}</div>
            <div className="mt-3 flex flex-col text-sm">
              <div className="flex justify-between border-b border-slate-100 py-2"><span>Cards</span><span className="tabular-nums">{money(cards)}</span></div>
              <div className="flex justify-between border-b border-slate-100 py-2"><span>Customers</span><span className="tabular-nums">{money(debtors.total_balance - cards)}</span></div>
              <div className="flex justify-between py-2"><span>Over 30 days</span><span className={`tabular-nums ${over30 > 0 ? "text-rose-700" : ""}`}>{money(over30)}</span></div>
            </div>
          </Card>
        )}
        <Card title="Fuel in tanks">
          <div className="mt-3 flex flex-col text-sm">
            {Object.entries(byProduct).map(([p, l]) => (
              <div key={p} className="flex justify-between border-b border-slate-100 py-2 last:border-0"><span>{p}</span><span className="tabular-nums">{l.toLocaleString()} L</span></div>
            ))}
            {!Object.keys(byProduct).length && <div className="py-2 text-slate-400">No dips yet</div>}
          </div>
          <Link to="/v2/stock" className="mt-2 inline-block text-sm font-semibold text-sky-700 hover:underline">Stock</Link>
        </Card>
      </div>
    </Page>
  );
}
